<template>
  <div class="radio-group" :class="computedClass">
    <BaseRadio
      v-for="(option, index) in props.options"
      :key="index"
      :label="option.label"
      :value="option.value"
      :name="name"
      :model-value="props.modelValue"
      @update:model-value="emit('update:modelValue', option.value)"
    />
  </div>
</template>

<script lang="ts" setup>
import { useAnimationShake } from '../../lib'
import BaseRadio from './index.vue'

interface Option {
  label: string
  value: string
}
interface Props {
  modelValue: string
  name?: string
  options: Option[]
  variant?: 'default' | 'light'
  direction?: 'row' | 'column'
  form?: 'default' | 'input'
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  direction: 'row',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const { shakeClass } = useAnimationShake(() => props.errorMessage)

const computedClass = computed(() => {
  return {
    [`radio-group--direction-${props.direction}`]: true,
    [`radio-group--form-${props.form}`]: props.form,
    [`radio-group--variant-${props.variant}`]: props.variant,
    'radio-group--error': props.errorMessage,
    [shakeClass.value]: true,
  }
})
</script>

<style lang="scss" scoped>
.radio-group {
  display: flex;
  gap: rem(12px);

  &--direction {
    &-row {
      flex-direction: row;
    }

    &-column {
      flex-direction: column;
    }
  }

  &--form {
    &-input {
      &:deep(.base-radio) {
        --radio-size: 20px;

        border-radius: rem(12px);
        min-height: 38px;
        padding: 0 rem(9px);
        display: flex;
        align-items: center;

        p {
          padding: rem(12px) 0;
        }

        > * {
          flex: 1;
        }
      }

      &:deep(.base-radio__wrapper) {
        align-self: center;
      }
    }
  }

  &--form-input.radio-group--variant {
    &-light {
      &:deep(.base-radio) {
        background-color: var(--color-surface-bg);
      }
    }
  }

  &--error:deep(.base-radio) {
    border: 2px solid var(--color-surface-danger);
  }
}
</style>
